import { useCallback, type ReactElement, type ReactNode } from "react";
import { Router } from "wouter";
import { type ClaimResult, useAroundNavBoundary } from "./useAroundNavBoundary.ts";

/** `/threads/123` and nothing deeper — `/threads/123/participants` is a card the
 *  canvas boundary inside this one claims first. */
const THREAD_PATH = /^\/threads\/(\d+)\/?$/;

/**
 * Navigation boundary for a link that points at another thread.
 *
 * A thread is not a card, so {@link CanvasNavBoundary} declines it. Claimed here
 * instead of falling through to `/threads/:id`, the shape thread-on-canvas
 * replaced.
 *
 * Sits outside the canvas boundary so cards still win: a participants link is
 * claimed in there and never reaches this.
 *
 * `onOpenThread` returns false where there is no canvas to open into — mobile,
 * where a thread really is its own page — and the link falls through to the
 * route as before.
 */
export const ThreadNavBoundary = ({
  onOpenThread,
  children,
}: {
  onOpenThread: (threadId: number) => boolean;
  children: ReactNode;
}): ReactElement => {
  const claim = useCallback(
    (to: string): ClaimResult => {
      const match = THREAD_PATH.exec(to);
      if (!match?.[1]) return "delegate";
      return onOpenThread(Number(match[1])) ? "claimed" : "delegate";
    },
    [onOpenThread],
  );

  const aroundNav = useAroundNavBoundary(claim);
  return <Router aroundNav={aroundNav}>{children}</Router>;
};
